import {
  Box,
  IconButton,
  Link as MuiLink,
  Typography,
  capitalize,
} from "@mui/material";
import { TemplateStat } from "contexts/Metrics";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext } from "react";
import { UpgradeDialogSource } from "schemas/dashboard";

import {
  Template,
  TemplateContentType,
} from "features/Influencer/ContactList/schema";
import { sanitize } from "utils/sanitize";
import { formatEpochTimestamp } from "utils/time";

import styles from "./styles";

interface TemplateCardProps {
  template: Template;
  emailCounts: { [key: number]: number };
  stat?: TemplateStat;
  onMenuOpen: (e: React.MouseEvent<HTMLElement>, template: Template) => void;
}

const formatPercentage = (value?: number) =>
  `${Math.round((value || 0) * 100)}%`;

export default function TemplateCard({
  template,
  emailCounts,
  stat,
  onMenuOpen,
}: TemplateCardProps) {
  const { subscription, setUpgradeDialogSource } =
    useContext(SubscriptionContext);

  const isOutreach = template?.contentType === TemplateContentType.OUTREACH;
  const inUseCount =
    template?.id && template.id in emailCounts ? emailCounts[template.id] : 0;

  return (
    <>
      <Box sx={styles.cardHeader}>
        <Typography
          component="span"
          sx={styles.cardBadge(template?.contentType)}
        >
          {capitalize(template?.contentType)}
        </Typography>
        {inUseCount > 0 && (
          <Typography sx={styles.cardModified}>
            <Box component="i" className="fa-regular fa-envelope" />{" "}
            {inUseCount} in use
          </Typography>
        )}
      </Box>

      <Typography sx={styles.cardTitle}>{template?.name}</Typography>

      {template?.subject && (
        <Box
          sx={styles.cardSubject}
          dangerouslySetInnerHTML={{ __html: sanitize(template.subject) }}
        />
      )}

      {template?.body && (
        <Box
          sx={styles.cardBody}
          dangerouslySetInnerHTML={{ __html: sanitize(template.body) }}
        />
      )}

      <Box sx={styles.cardDivider} />

      {isOutreach && (
        <Box sx={styles.cardStatsRow}>
          <Box sx={styles.cardStat}>
            <Typography
              sx={styles.cardStatValue(Boolean(subscription?.isSubscribed))}
            >
              {subscription?.isSubscribed ? (
                formatPercentage(stat?.openedPercentage)
              ) : (
                <MuiLink
                  sx={styles.statUpgrade}
                  onClick={(e) => {
                    e.stopPropagation();
                    setUpgradeDialogSource(UpgradeDialogSource.email_tracking);
                  }}
                >
                  Upgrade
                </MuiLink>
              )}
            </Typography>
            <Typography sx={styles.cardStatLabel}>Open rate</Typography>
          </Box>
          <Box sx={styles.cardStat}>
            <Typography sx={styles.cardStatValue(true)}>
              {formatPercentage(stat?.respondedPercentage)}
            </Typography>
            <Typography sx={styles.cardStatLabel}>Reply rate</Typography>
          </Box>
          <Box sx={styles.cardStat}>
            <Typography sx={styles.cardStatValue(false)}>
              {(stat?.outreachCount ?? 0).toLocaleString()}
            </Typography>
            <Typography sx={styles.cardStatLabel}>Pitches sent</Typography>
          </Box>
        </Box>
      )}

      <Box sx={styles.cardFooter}>
        <Typography sx={styles.cardModified}>
          {template?.updatedAt
            ? `Modified ${formatEpochTimestamp({
                epochTimestamp: template.updatedAt,
                addYear: true,
              })}`
            : "—"}
        </Typography>
        <IconButton
          sx={styles.cardMenuButton}
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onMenuOpen(e, template);
          }}
        >
          <Box component="i" className="fa-solid fa-ellipsis" />
        </IconButton>
      </Box>
    </>
  );
}
